import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { ProductSearchBuilder, Searcher } from '@relewise/client';
import { FALLBACK_UNIT_OF_MEASURE, RELEWISE_DISPLAYED_AT_LOCATION } from '../const';
import { ProductColor, ProductBuyboxData, VariantDetails } from '../types/mapped';
import { ProductData, RelewiseProductResult, RelewiseVariantResult } from '../types/relewiseData';
import { BaseRelewiseService, InitializedRelewiseService } from './BaseRelewiseService/BaseRelewiseService';
import { RelewiseMappingService } from './MappingService';

type CompatibleValuesMap = Record<string, string[]>;
interface InitializedService extends InitializedRelewiseService {
    searcher: Searcher;
    relewiseMappingService: RelewiseMappingService;
}
export class RelewiseProductService extends BaseRelewiseService<InitializedService> {
    protected searcher: Searcher | undefined;
    protected relewiseMappingService: RelewiseMappingService | undefined;

    constructor(request: IRequestContext, telemetry: ITelemetry) {
        super(request, telemetry, 'Product Service');

        if (!this.isEnabled()) {
            return;
        }

        this.relewiseMappingService = new RelewiseMappingService(request, telemetry);
        this.searcher = new Searcher(this.relewiseDatasetId, this.relewiseApiKey, {
            serverUrl: this.relewiseUrl
        });
    }

    public async getProductDetails(productOrVariantRecordId: string | undefined): Promise<ProductBuyboxData | undefined> {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const settings = await this.getApiRequestSettings(RELEWISE_DISPLAYED_AT_LOCATION.PDP);

            const builder = new ProductSearchBuilder(settings)
                .pagination(p => p.setPageSize(1))
                .setSelectedProductProperties({
                    displayName: true,
                    dataKeys: [
                        'ItemId',
                        'RecordId',
                        'Description',
                        'FriendlyDescription',
                        'Composition',
                        'CareBleaching',
                        'CareDryCleaning',
                        'CareIroning',
                        'CareNaturalDrying',
                        'CareTumbleDrying',
                        'CareWashing',
                        'Images',
                        `Sizes_${this.legalEntityId}`
                    ],
                    allVariants: true,
                    brand: true
                })
                .setSelectedVariantProperties({
                    displayName: true,
                    pricing: true,
                    dataKeys: [
                        'Color',
                        'Size',
                        'ColorHexCode',
                        'ColorRefinerGroup',
                        'RecordId',
                        'PrimaryImageUrl',
                        'RelativeUrl',
                        'AdditionalImageUrls',
                        'LegalEntities',
                        `StockCount_${this.legalEntityId}`,
                        `PrimarySizeVariant_${this.legalEntityId}`,
                        `ProductLabels_${this.legalEntityId}`,
                        `InAssortment_${this.legalEntityId}`,
                        `MemberPrice_${this.legalEntityId}`
                    ]
                })
                .filters(f => {
                    this.addProductEnvironmentFilter(f);
                    f.addProductDataFilter(
                        'LookupRecordIds',
                        b =>
                            b.addContainsCondition({
                                value: productOrVariantRecordId,
                                type: 'Double',
                                isCollection: true
                            }),
                        true,
                        true
                    );
                    f.addProductDataFilter(
                        'LegalEntities',
                        b =>
                            b.addContainsCondition({
                                value: this.legalEntityId,
                                type: 'String',
                                isCollection: true
                            }),
                        true,
                        true
                    );
                });

            const response = await this.searcher.searchProducts(builder.build());
            const productResult = response?.results?.[0];

            if (!productResult) {
                throw new Error(`No product or variant found in Relewise for RecordId: ${productOrVariantRecordId}`);
            }

            const { allVariants, ...restProduct } = productResult;

            // When requesting all variants of a product, it is not possible
            // to filter variants on the request, so we must filter after the fact
            const filteredVariants = this.filterVariantsInLegalEntity(allVariants as RelewiseVariantResult[] | undefined);

            if (!filteredVariants?.length) {
                throw new Error(
                    `No variants available in the current legal entity for RecordId: ${productOrVariantRecordId}. Master product is marked as existing in Legal Entity, please revise Relewise data.`
                );
            }

            const { compatibleSizesByColor, allColors, variantsByKey, allSizes } = this.buildProductDataMaps(
                restProduct as RelewiseProductResult | undefined,
                filteredVariants
            );
            const productMetaInfo = this.relewiseMappingService.mapRelewiseProductToProductMetaInfo(
                restProduct.data as ProductData | undefined
            );

            const isRequestingVariant = productOrVariantRecordId !== String(restProduct.data?.RecordId.value);
            let requestedVariantKey: string | undefined;
            if (filteredVariants && isRequestingVariant) {
                const requestedVariant = filteredVariants.find(v => String(v.data?.RecordId?.value) === productOrVariantRecordId);
                requestedVariantKey = requestedVariant?.variantId ?? undefined;
            }

            const result: ProductBuyboxData = {
                productItemId: restProduct.data?.ItemId.value ?? '',
                productRecordId: restProduct.data?.RecordId.value,
                relewiseProductId: restProduct.productId ?? undefined,
                displayName: restProduct.displayName ?? '',
                productMetaInfo,
                variantsByKey,
                compatibleSizesByColor,
                allColors,
                brand: restProduct.brand?.displayName ?? undefined,
                allSizes,
                requestedVariantKey,
                unitOfMeasure: restProduct.data?.UnitOfMeasure?.value ?? FALLBACK_UNIT_OF_MEASURE,
                masterProductImages: restProduct.data?.Images?.value?.$values ?? []
            };

            return result;
        } catch (err) {
            this.logError(`getProductDetails request failed: ${err instanceof Error ? err.message : String(err)}`);
            return;
        }
    }

    private filterVariantsInLegalEntity(allVariants: RelewiseVariantResult[] | undefined): RelewiseVariantResult[] | undefined {
        if (!this.isEnabled()) {
            return;
        }

        const le = this.legalEntityId;
        return allVariants?.filter(variant => {
            const legalEntities = variant.data?.LegalEntities?.value?.$values ?? [];
            return legalEntities.includes(le);
        });
    }

    private buildProductDataMaps(
        product: RelewiseProductResult | undefined,
        allVariants?: RelewiseVariantResult[] | undefined
    ): {
        compatibleSizesByColor: CompatibleValuesMap;
        variantsByKey: Record<string, VariantDetails>;
        allColors: ProductColor[];
        allSizes: string[] | undefined;
    } {
        const variantsByKey: Record<string, VariantDetails> = {};
        const compatibleSizesByColorMap = new Map<string, Set<string>>();
        const allColorsMap = new Map<string, ProductColor>();

        allVariants?.forEach(variant => {
            const color = variant.data?.Color?.value;
            const size = variant.data?.Size?.value;
            const hex = variant.data?.ColorHexCode?.value;
            const colorRefinerGroup = variant.data?.ColorRefinerGroup?.value;
            if (!color || !hex) {
                return;
            }

            if (variant.variantId && product && this.isEnabled()) {
                const mappedVariant = this.relewiseMappingService.mapRelewiseVariantToVariantDetails(product, variant);
                if (mappedVariant) {
                    variantsByKey[variant.variantId] = mappedVariant;
                }
            }

            if (!allColorsMap.has(color)) {
                allColorsMap.set(color, {
                    name: color,
                    hex,
                    colorRefinerGroup
                });
            }

            if (!size) {
                return;
            }

            const sizes = compatibleSizesByColorMap.get(color) ?? new Set<string>();
            sizes.add(size);
            compatibleSizesByColorMap.set(color, sizes);
        });

        const allProductSizes = product?.data?.[`Sizes_${this.legalEntityId}`]?.value?.$values;
        const compatibleSizesByColor: CompatibleValuesMap = {};
        for (const [color, sizes] of compatibleSizesByColorMap.entries()) {
            compatibleSizesByColor[color] = Array.from(sizes).sort((a, b) => {
                if (!allProductSizes) {
                    return 0;
                }
                return allProductSizes.indexOf(a) - allProductSizes.indexOf(b);
            });
        }

        return {
            compatibleSizesByColor,
            variantsByKey,
            allColors: Array.from(allColorsMap.values()),
            allSizes: allProductSizes
        };
    }
}
